import React, { useEffect, useRef, useState } from "react";
import { useProject, DEFAULT_ADJ } from "../state/projectStore";

// Render a single piece (cut frame) with the image moved INSIDE the frame.
// All coords in mm relative to the page. pxPerMm scales them on screen.
// `pageOffset` is the global block shift (mm) for the current page; it shifts
// the visual position of the slot but the frame itself remains a fixed-size
// "cutting mark" reference (the cut is what we shift, not the image).
export default function Frame({ slot, pxPerMm, pageOffset = { x: 0, y: 0 } }) {
  const { state, updateSlot, selectSlot } = useProject();
  const adj = state.slotAdjustments[slot.index] || DEFAULT_ADJ;
  const img = adj.imageId ? state.images.find((i) => i.id === adj.imageId) : null;
  const selected = state.selectedSlot === slot.index;

  const [drag, setDrag] = useState(null);
  const adjRef = useRef(adj);
  adjRef.current = adj;

  // Pixel coords of frame (with page offset applied for shifting).
  const bleed = state.bleed;
  const offX = pageOffset.x || 0;
  const offY = pageOffset.y || 0;
  const bleedX = (slot.bleed_x_mm + offX) * pxPerMm;
  const bleedY = (slot.bleed_y_mm + offY) * pxPerMm;
  const bleedW = slot.bleed_w_mm * pxPerMm;
  const bleedH = slot.bleed_h_mm * pxPerMm;

  const cutX = (slot.x_mm + offX) * pxPerMm;
  const cutY = (slot.y_mm + offY) * pxPerMm;
  const cutW = slot.width_mm * pxPerMm;
  const cutH = slot.height_mm * pxPerMm;

  // Image inside bleed rect with same algorithm as PDF export.
  let imageStyle = null;
  if (img) {
    const fit = adj.fit || "fill";
    const zoom = Math.max(0.05, adj.zoom || 1);
    const offsetX = (adj.offset_x || 0) * pxPerMm;
    const offsetY = (adj.offset_y || 0) * pxPerMm;
    const sFit = Math.min(bleedW / img.width_px, bleedH / img.height_px);
    const sFill = Math.max(bleedW / img.width_px, bleedH / img.height_px);
    const sBase = fit === "fit" ? sFit : sFill;
    const sX = adj.distort ? sBase * (adj.scaleX || 1) : sBase * zoom;
    const sY = adj.distort ? sBase * (adj.scaleY || 1) : sBase * zoom;
    const drawW = img.width_px * sX;
    const drawH = img.height_px * sY;
    const cx = bleedW / 2 + offsetX;
    const cy = bleedH / 2 + offsetY;
    imageStyle = {
      position: "absolute",
      left: `${cx - drawW / 2}px`,
      top: `${cy - drawH / 2}px`,
      width: `${drawW}px`,
      height: `${drawH}px`,
      // Override Tailwind's preflight `img { max-width: 100%; height: auto }` so
      // the image can exceed the frame (the frame only acts as a visual clip).
      maxWidth: "none",
      maxHeight: "none",
      // Default `object-fit: fill` stretches to the explicit width/height we
      // already computed; this preserves the math for both proportional zoom
      // and non-proportional distortion.
      objectFit: "fill",
      transform: adj.rotation ? `rotate(${adj.rotation}deg)` : "none",
      transformOrigin: "center center",
      pointerEvents: "none",
      userSelect: "none",
    };
  }

  // Drag-to-move image inside the frame. Marco se mantiene fijo; sólo cambian
  // offset_x/offset_y del adjustment.
  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const dxPx = e.clientX - drag.startX;
      const dyPx = e.clientY - drag.startY;
      updateSlot(slot.index, {
        offset_x: Math.round((drag.initX + dxPx / pxPerMm) * 100) / 100,
        offset_y: Math.round((drag.initY + dyPx / pxPerMm) * 100) / 100,
      });
    };
    const onUp = () => setDrag(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [drag, pxPerMm, slot.index, updateSlot]);

  const handleMouseDown = (e) => {
    e.stopPropagation();
    selectSlot(slot.index);
    if (img) {
      const current = adjRef.current;
      setDrag({
        startX: e.clientX,
        startY: e.clientY,
        initX: current.offset_x || 0,
        initY: current.offset_y || 0,
      });
    }
  };

  return (
    <>
      {/* BLEED rectangle (image clipped inside) */}
      <div
        onMouseDown={handleMouseDown}
        onClick={(e) => {
          // Fallback for synthetic .click() events (which don't fire mousedown)
          e.stopPropagation();
          selectSlot(slot.index);
        }}
        data-testid={`slot-${slot.index}`}
        style={{
          position: "absolute",
          left: `${bleedX}px`,
          top: `${bleedY}px`,
          width: `${bleedW}px`,
          height: `${bleedH}px`,
          overflow: "hidden",
          cursor: img ? (drag ? "grabbing" : "grab") : "pointer",
          background: img ? "transparent" : "#F3F4F6",
          outline: selected ? "2px solid #00E5FF" : "none",
          outlineOffset: "0px",
        }}
      >
        {img ? (
          <img src={img.dataUrl} alt="" style={imageStyle} draggable={false} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[9px] font-mono text-[#9CA3AF]">
            #{slot.index + 1}
          </div>
        )}
      </div>

      {/* CUT FRAME (always shown as a dashed pink border indicating the cutting line). */}
      <div
        style={{
          position: "absolute",
          left: `${cutX}px`,
          top: `${cutY}px`,
          width: `${cutW}px`,
          height: `${cutH}px`,
          border: "1px dashed rgba(255, 0, 127, 0.7)",
          pointerEvents: "none",
          boxSizing: "border-box",
        }}
        data-testid={`cut-frame-${slot.index}`}
      />

      {/* BLEED border (only when bleed > 0) */}
      {bleed > 0 && (
        <div
          style={{
            position: "absolute",
            left: `${bleedX}px`,
            top: `${bleedY}px`,
            width: `${bleedW}px`,
            height: `${bleedH}px`,
            border: "1px dashed rgba(255, 179, 0, 0.5)",
            pointerEvents: "none",
            boxSizing: "border-box",
          }}
        />
      )}

      {/* Optional thin black border on cut */}
      {state.cutMarks.showBorderFrame && (
        <div
          style={{
            position: "absolute",
            left: `${cutX}px`,
            top: `${cutY}px`,
            width: `${cutW}px`,
            height: `${cutH}px`,
            border: "1px solid #000",
            pointerEvents: "none",
            boxSizing: "border-box",
          }}
        />
      )}
    </>
  );
}

// SVG corner cut marks for the whole page.
// Kind: 'piece' (per-piece corners) or 'block_outer' (only outer corners of the
// whole imposition block on the sheet).
export function CutMarksOverlay({ slots, cutMarks, pxPerMm, pageWmm, pageHmm, pageOffset = { x: 0, y: 0 } }) {
  if (!cutMarks.enabled || !slots.length) return null;
  const lenPx = cutMarks.length_mm * pxPerMm;
  const distPx = cutMarks.distance_mm * pxPerMm;
  const stroke = Math.max(0.5, cutMarks.weight_pt * pxPerMm * 0.35);
  const offX = (pageOffset.x || 0) * pxPerMm;
  const offY = (pageOffset.y || 0) * pxPerMm;

  // Build the set of rectangles whose corners we draw marks on.
  let rects;
  if (cutMarks.kind === "block_outer") {
    // Compute the outer bounding box across all slot cut rects.
    const minX = Math.min(...slots.map((s) => s.x_mm));
    const minY = Math.min(...slots.map((s) => s.y_mm));
    const maxX = Math.max(...slots.map((s) => s.x_mm + s.width_mm));
    const maxY = Math.max(...slots.map((s) => s.y_mm + s.height_mm));
    rects = [{ x: minX * pxPerMm + offX, y: minY * pxPerMm + offY, w: (maxX - minX) * pxPerMm, h: (maxY - minY) * pxPerMm, key: "outer" }];
  } else {
    rects = slots.map((s) => ({
      x: s.x_mm * pxPerMm + offX,
      y: s.y_mm * pxPerMm + offY,
      w: s.width_mm * pxPerMm,
      h: s.height_mm * pxPerMm,
      key: `p${s.index}`,
    }));
  }

  return (
    <svg
      width={pageWmm * pxPerMm}
      height={pageHmm * pxPerMm}
      style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none" }}
      data-testid="cut-marks-overlay"
    >
      {rects.flatMap((r) => {
        const corners = [
          { cx: r.x, cy: r.y, sx: -1, sy: -1 },
          { cx: r.x + r.w, cy: r.y, sx: 1, sy: -1 },
          { cx: r.x, cy: r.y + r.h, sx: -1, sy: 1 },
          { cx: r.x + r.w, cy: r.y + r.h, sx: 1, sy: 1 },
        ];
        return corners.flatMap((c, i) => [
          <line
            key={`hx${r.key}-${i}`}
            x1={c.cx + c.sx * distPx}
            y1={c.cy}
            x2={c.cx + c.sx * (distPx + lenPx)}
            y2={c.cy}
            stroke="#000"
            strokeWidth={stroke}
          />,
          <line
            key={`vy${r.key}-${i}`}
            x1={c.cx}
            y1={c.cy + c.sy * distPx}
            x2={c.cx}
            y2={c.cy + c.sy * (distPx + lenPx)}
            stroke="#000"
            strokeWidth={stroke}
          />,
        ]);
      })}
    </svg>
  );
}
